// ═══════════════════════════════════════════════════════════════════════════
// EmailVerificationPage — Phase 2.1 verification gate.
//
// Shown to users who have signed up but not yet clicked the verification
// link in their inbox. Two visible affordances:
//
//   1. A clear status line ("we've sent a link to <email> — check spam too")
//   2. A "Resend email" button with a 60-second cooldown that mirrors
//      Supabase's server-side rate limit, so users don't hammer the server
//      and don't see opaque rate-limit errors.
//
// The page polls Supabase every 5 seconds for `email_confirmed_at` so the
// UI flips to "verified" automatically when the user clicks the link in a
// different tab — no manual refresh required. The polling is light (one
// `getUser` round-trip every 5s) and stops automatically once the email
// flips to verified, on unmount, and on tab hide (Page Visibility API).
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { resendVerificationEmail } from '../../services/authApi';
import { mapAuthError } from '../../services/errorMap';
import { analytics } from '../../services/analytics';
import { supabase } from '../../lib/supabase/client';
import { AuthLayout } from '../../components/auth/AuthLayout';
import { useCountdown } from '../../hooks/useCountdown';
import {
  AUTH_RESEND_COOLDOWN_SECONDS,
  EMAIL_VERIFICATION_POLL_MS,
} from '../../config/authTimeouts';
import styles from './auth-page.module.css';

export function EmailVerificationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const { secondsLeft: resendCooldown, start: startCooldown, active: inCooldown } =
    useCountdown();
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendError, setResendError] = useState<string | null>(null);

  // Step 1: redirect away if there's nothing to verify (anonymous user) or
  // if the user is already verified. We do this in a memo so the effect
  // below doesn't trip on its own state changes.
  const redirectTarget = useMemo(() => {
    if (!isAuthenticated) return '/auth/login';
    if (user?.emailVerified) return '/';
    return null;
  }, [isAuthenticated, user?.emailVerified]);

  useEffect(() => {
    if (redirectTarget) {
      navigate(redirectTarget, { replace: true });
    }
  }, [redirectTarget, navigate]);

  // Step 2: detect email verification.
  //
  // Two mechanisms run in parallel; whichever fires first wins:
  //
  //   a) `supabase.auth.onAuthStateChange` for USER_UPDATED / SIGNED_IN
  //      events. These fire instantly when the user clicks the
  //      verification link in the SAME tab, and (via the global auth
  //      listener + BroadcastChannel installed by initAuthListeners())
  //      also when verification happens in ANOTHER tab on this device.
  //      No HTTP round-trip — pure event-driven.
  //
  //   b) A 15s polling fallback for the case where the verification
  //      happened on another DEVICE (e.g. the user opened the email on
  //      their phone). Pauses automatically when the tab is hidden
  //      (Page Visibility API) so we don't burn Supabase quota for
  //      backgrounded tabs.
  //
  // Both paths converge on the same handler — `handleVerified()` is
  // idempotent, so a race between the two is harmless.
  useEffect(() => {
    if (!isAuthenticated || user?.emailVerified) return;

    let cancelled = false;
    const handleVerified = () => {
      if (cancelled) return;
      cancelled = true;
      analytics.event('auth_email_verified');
      navigate('/', { replace: true });
    };

    // (a) Push-based detection — the cheap path.
    const { data: subscription } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (
          (event === 'USER_UPDATED' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') &&
          session?.user?.email_confirmed_at
        ) {
          handleVerified();
        }
      },
    );

    // (b) Polling fallback for cross-device verifications.
    const tick = async () => {
      if (cancelled || document.hidden) return;
      try {
        const { data } = await supabase.auth.getUser();
        if (cancelled) return;
        if (data?.user?.email_confirmed_at) {
          handleVerified();
        }
      } catch {
        // Silent — the next tick will retry.
      }
    };
    const id = window.setInterval(tick, EMAIL_VERIFICATION_POLL_MS);
    // First tick immediately so a user who already verified before
    // landing on this page sees the result without waiting 15s.
    tick();

    return () => {
      cancelled = true;
      window.clearInterval(id);
      subscription?.subscription?.unsubscribe?.();
    };
  }, [isAuthenticated, user?.emailVerified, navigate]);

  // Step 3: cooldown timer is handled by `useCountdown()` above. The hook
  // owns the setTimeout chain; we just call `startCooldown(N)` after a
  // successful resend.

  const handleResend = async () => {
    if (!user?.email || inCooldown || resendStatus === 'sending') return;
    setResendStatus('sending');
    setResendError(null);
    analytics.event('auth_email_resend_request');
    try {
      await resendVerificationEmail(user.email);
      setResendStatus('sent');
      startCooldown(AUTH_RESEND_COOLDOWN_SECONDS);
      analytics.event('auth_email_resend_success');
    } catch (err) {
      const mapped = mapAuthError(err);
      setResendError(t(mapped.messageKey, mapped.messageDefault));
      setResendStatus('error');
      analytics.event('auth_email_resend_failed', { code: mapped.code });
    }
  };

  if (!isAuthenticated || user?.emailVerified) {
    return null; // about to redirect
  }

  return (
    <AuthLayout cardMaxWidth={480} cardPadding="32px" background="var(--bg)">
      <div className={styles.doneStack}>
        <div aria-hidden="true" className={styles.envelope}>✉️</div>
        <h1 className={styles.title}>
          {t('auth.actions.verifyEmail', 'Verify your email')}
        </h1>
        <p className={styles.subtitle}>
          {t('auth.errors.emailNotVerified', 'Vérifie ton email avant de te connecter.')}
        </p>
        {user?.email && (
          <p className={styles.userEmail}>
            {user.email}
          </p>
        )}
        <p className={styles.helpText}>
          {t('auth.hints.checkInbox', 'Un lien de vérification a été envoyé à ta boîte de réception.')}
        </p>

        <button
          type="button"
          onClick={handleResend}
          disabled={inCooldown || resendStatus === 'sending'}
          aria-busy={resendStatus === 'sending'}
          className={inCooldown ? styles.resendButtonOutline : styles.resendButton}
        >
          {resendStatus === 'sending'
            ? t('common.loading', 'Loading…')
            : inCooldown
            ? t('auth.hints.tryAgainIn', { defaultValue: 'Try again in {{minutes}} min.', minutes: Math.ceil(resendCooldown / 60) })
              + (resendCooldown < 60 ? ` (${resendCooldown}s)` : '')
            : t('auth.actions.resend', 'Resend email')}
        </button>

        {resendStatus === 'sent' && (
          <p className={styles.successTextSmall}>
            ✓ {t('auth.hints.checkInbox', 'Un lien de vérification a été envoyé à ta boîte de réception.')}
          </p>
        )}
        {resendError && (
          <p className={styles.resendError}>
            {resendError}
          </p>
        )}

        <div className={styles.footer}>
          <Link to="/auth/login" className={styles.linkAccent}>
            ← {t('auth.actions.login', 'Sign in')}
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
}

export default EmailVerificationPage;
